import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/tr";
import { Box, CircularProgress, useMediaQuery, useTheme } from "@mui/material";
import { useBaseQuery } from "src/api/core/useBaseQuery";
import GridMenu from "src/modules/nurse/order-medicine/components/grid-menu";
import CalendarBoard from "src/modules/nurse/order-medicine/components/calendar-board";
import type { PatientOrderAndOtherTreatmentResponse } from "src/modules/nurse/order-medicine/models";

type CategoryKey = "all" | "medicine" | "other" | "blood";

type TreatmentsPanelProps = {
  patientId?: string | number;
  upn?: string;
  patientName?: string;
};

export default function TreatmentsPanel({ patientId, upn }: Readonly<TreatmentsPanelProps>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const visibleColumnCount = isMobile ? 1 : isTablet ? 2 : 4;
  const [selectedCategory, setSelectedCategory] = useState<CategoryKey>("all");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [startColumnIndex, setStartColumnIndex] = useState(0);
  const [nowTick, setNowTick] = useState(() => Date.now());

  const getTypeFromCategory = (category: string): number => {
    switch (category) {
      case "medicine":
        return 2;
      case "other":
        return 3;
      case "blood":
        return 4;
      default:
        return 1;
    }
  };

  const apiEndpoint = useMemo(() => {
    if (!patientId || !selectedDate) return "";

    const type = getTypeFromCategory(selectedCategory);
    const searchDate = dayjs(selectedDate).format("YYYY.MM.DD");

    return `/nurse/api/Order/GetPatientOrderAndOtherTreatments?patientId=${patientId}&type=${type}&SearchDate=${searchDate}`;
  }, [patientId, selectedDate, selectedCategory]);

  const { data: apiData, isLoading, refetch } = useBaseQuery<PatientOrderAndOtherTreatmentResponse[]>({
    endpoint: apiEndpoint,
    queryKey: ["patient-table-order-treatments", patientId, selectedDate, selectedCategory],
    options: {
      enabled: !!apiEndpoint,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  });

  useEffect(() => {
    const timer = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);

  const normalizedApiData = useMemo<PatientOrderAndOtherTreatmentResponse[]>(() => {
    if (Array.isArray(apiData)) {
      return apiData;
    }

    if (apiData && Array.isArray((apiData as any).data)) {
      return (apiData as any).data as PatientOrderAndOtherTreatmentResponse[];
    }

    return [];
  }, [apiData]);

  const transformApiDataToBoardColumns = (
    source: PatientOrderAndOtherTreatmentResponse[],
    category: string,
    filter: string,
    currentDate: dayjs.Dayjs
  ) => {
    if (!source.length) return [];

    const itemsBySlot: Record<string, { plandate: string; planhourshow: string; items: any[] }> = {};

    source.forEach((item) => {
      const plandate = dayjs(item.plandate);
      const hour = parseInt(item.planhourshow, 10);

      if (Number.isNaN(hour)) return;

      const slotKey = `${plandate.format("YYYY-MM-DD")}-${hour}`;
      if (!itemsBySlot[slotKey]) {
        itemsBySlot[slotKey] = {
          plandate: item.plandate,
          planhourshow: item.planhourshow,
          items: [],
        };
      }

      item.patientorderandothertreatmentdetail.forEach((detail) => {
        itemsBySlot[slotKey].items.push(detail);
      });
    });

    return Object.keys(itemsBySlot)
      .sort((a, b) => {
        const [dateA, hourA] = a.split("-");
        const [dateB, hourB] = b.split("-");
        if (dateA !== dateB) return dateA.localeCompare(dateB);
        return parseInt(hourA, 10) - parseInt(hourB, 10);
      })
      .map((slotKey) => {
        const slotData = itemsBySlot[slotKey];
        const slotDay = dayjs(slotData.plandate);
        const hour = parseInt(slotData.planhourshow, 10);
        const time = slotDay.hour(hour).minute(0);
        const label = `${slotDay.date()} ${slotDay.locale("tr").format("MMMM").toUpperCase()}  ${time.format("HH:mm")}`;

        const items = slotData.items
          .map((detail, idx) => {
            let cardType: "medicine" | "medicine-mix" | "other" | "blood" = "medicine";
            let itemId = `${slotKey}-${idx}`;
            let itemTime = time.format("HH:mm");

            if (detail.orderdetail) {
              const order = detail.orderdetail;
              itemId = `order-${order.srumid}-${order.seqno}-${idx}`;
              if (order.planhour) {
                itemTime = dayjs(order.planhour).format("HH:mm");
              }
              cardType = order.ismixture === "1" || String(order.ismixture) === "1" ? "medicine-mix" : "medicine";
            } else if (detail.othertreatment) {
              const otherTreatment = detail.othertreatment;
              itemId = `other-${otherTreatment.udid}-${idx}`;
              if (otherTreatment.startdate) {
                itemTime = dayjs(otherTreatment.startdate).format("HH:mm");
              }
              cardType = otherTreatment.isblood === "1" || String(otherTreatment.isblood) === "1" ? "blood" : "other";
            }

            return {
              id: itemId,
              time: itemTime,
              title: detail.orderdetail?.reqmatname?.trim() || detail.orderdetail?.matname?.trim() || detail.othertreatment?.name || detail.othertreatment?.srntype || "",
              cardType,
              rawData: detail,
              planHourDate: detail.orderdetail?.planhour
                ? dayjs(detail.orderdetail.planhour)
                : detail.othertreatment?.startdate
                  ? dayjs(detail.othertreatment.startdate)
                  : time,
            };
          })
          .filter((item) => {
            if (category !== "all") {
              if (category === "medicine" && item.cardType !== "medicine" && item.cardType !== "medicine-mix") return false;
              if (category === "other" && item.cardType !== "other") return false;
              if (category === "blood" && item.cardType !== "blood") return false;
            }

            if (filter === "all") return true;

            const orderDetail = item.rawData?.orderdetail;
            const now = dayjs();

            if (!orderDetail && item.rawData?.othertreatment) {
              const isApplied = item.rawData.othertreatment.ismake === "1" || String(item.rawData.othertreatment.ismake) === "1";
              if (filter === "pending") return !isApplied && !item.planHourDate.isBefore(now);
              if (filter === "applied") return isApplied;
              if (filter === "past") return !isApplied && item.planHourDate.isBefore(now);
              return true;
            }

            const state = orderDetail?.state;
            if (filter === "pending") return state === 0 && !item.planHourDate.isBefore(now);
            if (filter === "applied") return state === 1;
            if (filter === "past") return state === 0 && item.planHourDate.isBefore(now);
            return true;
          });

        return {
          id: `col-${slotKey}`,
          label,
          items,
          isDifferentDate: !slotDay.isSame(currentDate, "day"),
          slotDateTime: time.toISOString(),
        };
      })
      .filter((column) => column.items.length > 0);
  };

  const allColumns = useMemo(
    () => transformApiDataToBoardColumns(normalizedApiData, selectedCategory, selectedFilter, dayjs(selectedDate || new Date()).startOf("day")),
    [normalizedApiData, selectedCategory, selectedFilter, selectedDate]
  );

  const activeGlobalIndex = useMemo(() => {
    const now = dayjs(nowTick);
    const selectedDay = dayjs(selectedDate || new Date()).startOf("day");
    const isToday = selectedDay.isSame(now.startOf("day"), "day");

    if (!isToday) {
      return -1;
    }

    return allColumns.findIndex((col) => {
          if (!col.slotDateTime) return false;
          const colTime = dayjs(col.slotDateTime);
          const diffMinutes = colTime.diff(now, "minute", true);

          return colTime.isSame(selectedDay, "day") && diffMinutes > 0 && diffMinutes <= 60;
        });
  }, [allColumns, selectedDate, nowTick]);

  useEffect(() => {
    const maxStartIndex = Math.max(0, allColumns.length - visibleColumnCount);
    const preferredStart = activeGlobalIndex >= 0 ? Math.min(Math.max(0, activeGlobalIndex - 1), maxStartIndex) : 0;
    setStartColumnIndex(preferredStart);
  }, [activeGlobalIndex, allColumns.length, visibleColumnCount, selectedCategory, selectedFilter, selectedDate, patientId]);

  const { boardColumns, highlightFromIndex } = useMemo(() => {
    if (allColumns.length === 0) {
      return { boardColumns: [], highlightFromIndex: -1 };
    }

    const maxStartIndex = Math.max(0, allColumns.length - visibleColumnCount);
    const normalizedStartIndex = Math.min(startColumnIndex, maxStartIndex);
    const visibleColumns = allColumns.slice(normalizedStartIndex, normalizedStartIndex + visibleColumnCount);
    const localHighlightIndex =
      activeGlobalIndex >= 0 ? visibleColumns.findIndex((col) => col.id === allColumns[activeGlobalIndex].id) : -1;

    return {
      boardColumns: visibleColumns,
      highlightFromIndex: localHighlightIndex,
    };
  }, [allColumns, activeGlobalIndex, startColumnIndex, visibleColumnCount]);

  const totalColumnCount = allColumns.length;

  return (
    <Box sx={{ mt: "18px", display: "flex", flexDirection: "column", gap: 2 }}>
      <GridMenu
        selectedCategory={selectedCategory}
        selectedFilter={selectedFilter}
        selectedDate={selectedDate}
        onCategoryChange={(value) => setSelectedCategory(value as CategoryKey)}
        onFilterChange={setSelectedFilter}
        onDateChange={setSelectedDate}
        isLoading={isLoading}
      />

      {isLoading ? (
        <Box sx={{ minHeight: 320, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff", borderRadius: "12px" }}>
          <CircularProgress size={42} sx={{ color: "#2979C3" }} />
        </Box>
      ) : (
        <CalendarBoard
          columns={boardColumns}
          onPrev={() => setStartColumnIndex((prev) => Math.max(0, prev - 1))}
          onNext={() =>
            setStartColumnIndex((prev) => Math.min(Math.max(0, totalColumnCount - visibleColumnCount), prev + 1))
          }
          highlightFromIndex={highlightFromIndex}
          patientId={patientId}
          upn={upn}
          refetch={refetch}
        />
      )}
    </Box>
  );
}
